import { MapUtils } from '../../utility/map-utils.js';
import {
  cloneDataOrNull,
  DataModel,
  DataModelBuilder,
  isSameData,
  SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import { PhotoReference } from './file-reference/photo-reference.js';

export class BusinessDetailsSettings extends DataModel {
  static readonly TYPE_ID: string = 'cd38acfa1f8b4629b423e3e60af4f2a5';

  private constructor(
    public readonly businessName: string,
    public readonly legalName: string,
    public readonly taxNumber: string,
    public readonly companyRegistrationNumber: string,
    public readonly registeredAddressPostalCode: string,
    public readonly registeredAddressCity: string,
    public readonly registeredAddressStreet: string,
    public readonly logo: PhotoReference | null,
    public readonly contactName: string,
    public readonly contactEmail: string,
    public readonly contactPhone: string,
    public readonly publicName: string,
    public readonly website: string,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessDetailsSettings.TYPE_ID;
  }

  static create(args: {
    businessName: string,
    legalName: string,
    taxNumber: string,
    companyRegistrationNumber: string,
    registeredAddressPostalCode: string,
    registeredAddressCity: string,
    registeredAddressStreet: string,
    logo: PhotoReference | null,
    contactName: string,
    contactEmail: string,
    contactPhone: string,
    publicName: string,
    website: string,
  }): BusinessDetailsSettings {
    return new BusinessDetailsSettings(
        args.businessName,
        args.legalName,
        args.taxNumber,
        args.companyRegistrationNumber,
        args.registeredAddressPostalCode,
        args.registeredAddressCity,
        args.registeredAddressStreet,
        args.logo,
        args.contactName,
        args.contactEmail,
        args.contactPhone,
        args.publicName,
        args.website,
    );
  }

  static empty(): BusinessDetailsSettings {
    return BusinessDetailsSettings.create({
      businessName: '',
      legalName: '',
      taxNumber: '',
      companyRegistrationNumber: '',
      registeredAddressPostalCode: '',
      registeredAddressCity: '',
      registeredAddressStreet: '',
      logo: null,
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      publicName: '',
      website: '',
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessDetailsSettings {
    return BusinessDetailsSettings.create({
      businessName: MapUtils.getString(map, 'businessName'),
      legalName: MapUtils.getString(map, 'legalName'),
      taxNumber: MapUtils.getString(map, 'taxNumber'),
      companyRegistrationNumber: MapUtils.getString(map, 'companyRegistrationNumber'),
      registeredAddressPostalCode: MapUtils.getString(map, 'registeredAddressPostalCode'),
      registeredAddressCity: MapUtils.getString(map, 'registeredAddressCity'),
      registeredAddressStreet: MapUtils.getString(map, 'registeredAddressStreet'),
      logo: context.fromDataOrNull<PhotoReference>(MapUtils.getRecordOrNull(map, 'logo')),
      contactName: MapUtils.getString(map, 'contactName'),
      contactEmail: MapUtils.getString(map, 'contactEmail'),
      contactPhone: MapUtils.getString(map, 'contactPhone'),
      publicName: MapUtils.getString(map, 'publicName'),
      website: MapUtils.getString(map, 'website'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessName': this.businessName,
      'legalName': this.legalName,
      'taxNumber': this.taxNumber,
      'companyRegistrationNumber': this.companyRegistrationNumber,
      'registeredAddressPostalCode': this.registeredAddressPostalCode,
      'registeredAddressCity': this.registeredAddressCity,
      'registeredAddressStreet': this.registeredAddressStreet,
      'logo': context.toDataOrNull(this.logo),
      'contactName': this.contactName,
      'contactEmail': this.contactEmail,
      'contactPhone': this.contactPhone,
      'publicName': this.publicName,
      'website': this.website,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    businessName = this.businessName,
    legalName = this.legalName,
    taxNumber = this.taxNumber,
    companyRegistrationNumber = this.companyRegistrationNumber,
    registeredAddressPostalCode = this.registeredAddressPostalCode,
    registeredAddressCity = this.registeredAddressCity,
    registeredAddressStreet = this.registeredAddressStreet,
    logo = cloneDataOrNull(this.logo),
    contactName = this.contactName,
    contactEmail = this.contactEmail,
    contactPhone = this.contactPhone,
    publicName = this.publicName,
    website = this.website,
  }: {
    businessName?: string,
    legalName?: string,
    taxNumber?: string,
    companyRegistrationNumber?: string,
    registeredAddressPostalCode?: string,
    registeredAddressCity?: string,
    registeredAddressStreet?: string,
    logo?: PhotoReference | null,
    contactName?: string,
    contactEmail?: string,
    contactPhone?: string,
    publicName?: string,
    website?: string,
  } = {}): this {
    return BusinessDetailsSettings.create({
      businessName,
      legalName,
      taxNumber,
      companyRegistrationNumber,
      registeredAddressPostalCode,
      registeredAddressCity,
      registeredAddressStreet,
      logo,
      contactName,
      contactEmail,
      contactPhone,
      publicName,
      website,
    }) as this;
  }

  isSame(other: BusinessDetailsSettings): boolean {
    if(other === this) {
      return true;
    }

    return other.businessName === this.businessName &&
      other.legalName === this.legalName &&
      other.taxNumber === this.taxNumber &&
      other.companyRegistrationNumber === this.companyRegistrationNumber &&
      other.registeredAddressPostalCode === this.registeredAddressPostalCode &&
      other.registeredAddressCity === this.registeredAddressCity &&
      other.registeredAddressStreet === this.registeredAddressStreet &&
      isSameData(other.logo, this.logo) &&
      other.contactName === this.contactName &&
      other.contactEmail === this.contactEmail &&
      other.contactPhone === this.contactPhone &&
      other.publicName === this.publicName &&
      other.website === this.website;
  }
}

export class BusinessDetailsSettingsBuilder extends DataModelBuilder<BusinessDetailsSettings> {
  build(context: SerializationContext, map: SerializedData): BusinessDetailsSettings {
    return BusinessDetailsSettings.fromMap(context, map);
  }
}
